import { getModel } from '../../model/registry'
import { useParams } from 'react-router-dom'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import useApi from '../../hooks/useApi'
import { useStore } from '../pageView/Page'
import MasterView from './MasterView'
import DetailView from './DetailView'
import MasterDetailTitle from './MasterDetailTitle'
import Loader from '../generic/Loader'
import styled from 'styled-components'

const MasterDetailContainer = styled.div`
    display: flex;
    flex-direction: column;
    gap: 1.5rem;
    width: 100%;
    max-width: ${({ width }) => (width ? width : "")};
    padding: 0rem;
`

const MasterDetailRow = styled.div`
    display: flex;
    flex-direction: column;
    gap: 1.5rem;
    width: 100%;
`

/**
 * MasterDetailView Component
 * 
 * Displays a master-detail view with:
 * - Master section: Shows the main entity (e.g., invoice) as an info card
 * - Detail section: Shows related entities (e.g., payments) as a card list
 * 
 * Configuration example (pageViewData.jsx):
 * invoicePaymentPage: {
 *     type: 'MasterDetailView',
 *     viewType: 'invoicePaymentMDView',
 *     masterEntity: 'invoice',
 *     detailEntity: 'invoicePayment',
 *     detailFilter: { invoiceId: 'id' },
 * }
 *
 * `detailFromMaster: true` in place of detailEntity/detailFilter makes the
 * master record its own single detail card. An expense is paid as a whole —
 * it has no payment tranches to list — so its matches hang off it directly.
 */
const MasterDetailView = ({ type, options, dispatch }) => {
    const { id } = useParams()
    const { getMe, postMe } = useApi()
    const queryClient = useQueryClient()
    
    // Get state from Page context
    const [state, setState] = useStore((s) => s)
    
    // Get page configuration
    const pageConfig = getModel('pageViewData')[type]
    
    // Early return if no config found
    if (!pageConfig) {
        return <div>Configuration not found for {type}</div>
    }
    
    // Get view configuration
    const viewConfig = getModel('masterDetailViewData')[pageConfig.view ?? pageConfig.viewType]
    
    if (!viewConfig) {
        return <div>View configuration not found for {pageConfig.view ?? pageConfig.viewType}</div>
    }
    
    // Get entity names from config
    const masterEntity = pageConfig.masterEntity
    const detailEntity = pageConfig.detailEntity
    const detailFromMaster = Boolean(pageConfig.detailFromMaster)
    
    // Validate required config
    if (!masterEntity) {
        return <div>masterEntity not configured for {type}</div>
    }
    
    if (!id) {
        return <div>No ID found in URL</div>
    }
    
    // Fetch master entity
    // staleTime: 0 ensures data is always refetched when navigating to this page,
    // so the user always sees the latest payment state.
    const masterQuery = useQuery({
        queryKey: [masterEntity, id],
        queryFn: () => getMe(`${masterEntity}/${id}`),
        enabled: Boolean(masterEntity && id),
        staleTime: 0,
    })
    
    // Build detail filter from master data
    const buildDetailFilter = () => {
        if (!pageConfig.detailFilter || !masterQuery.data?.data) return null
        
        const filter = {}
        Object.entries(pageConfig.detailFilter).forEach(([key, value]) => {
            // If value is 'id', use the master entity's id
            if (value === 'id') {
                filter[key] = masterQuery.data.data.id
            } else {
                // Otherwise use the value directly (could be a field path)
                filter[key] = masterQuery.data.data[value]
            }
        })
        return filter
    }
    
    // Build detail endpoint with filter
    const buildDetailEndpoint = () => {
        const filter = buildDetailFilter()
        if (!filter || !detailEntity) return null
        
        const filterParts = Object.entries(filter).map(([key, value]) => `${key}:${value}`)
        return `${detailEntity}?filter=${filterParts.join(';')}`
    }
    
    // Fetch detail entities
    // staleTime: 0 ensures data is always refetched when navigating to this page.
    const detailEndpoint = buildDetailEndpoint()
    const detailQuery = useQuery({
        queryKey: [detailEntity, id, 'detail'],
        queryFn: () => getMe(detailEndpoint),
        enabled: Boolean(detailEndpoint) && masterQuery.isSuccess && !detailFromMaster,
        staleTime: 0,
    })
    
    // Action handler for detail line actions
    const handleAction = async (action, detailItem, lineItem) => {
        // If action is a simple refresh request (from StateForm onUpdate)
        if (!action) {
            // Just refetch queries
            await Promise.all([
                queryClient.refetchQueries({ queryKey: [masterEntity, id] }),
                ...(detailFromMaster ? [] : [queryClient.refetchQueries({ queryKey: [detailEntity, id, 'detail'] })])
            ])
            return
        }
        
        if (!action.path || !action.payload) {
            console.error('Action missing path or payload configuration')
            return
        }
        
        // Build payload by resolving paths from state
        const payload = {}
        Object.entries(action.payload).forEach(([key, path]) => {
            // Handle different path prefixes
            if (path.startsWith('line.')) {
                // Extract from line item
                const fieldPath = path.substring(5)
                payload[key] = fieldPath.split('.').reduce((obj, prop) => obj?.[prop], lineItem)
            } else if (path === 'id') {
                // Use detail item's id
                payload[key] = detailItem?.id
            } else {
                // Try to resolve from master data
                payload[key] = path.split('.').reduce((obj, prop) => obj?.[prop], masterQuery.data?.data)
            }
        })
        
        // Replace :id in path with detail item id
        const resolvedPath = action.path.replace(':id', detailItem?.id)
        
        try {
            const result = await postMe(resolvedPath, payload)
            if (result && !result.error) {
                // Build list of queries to refetch
                const refetchPromises = []
                
                // Always refetch current master and detail queries
                refetchPromises.push(queryClient.refetchQueries({ queryKey: [masterEntity, id] }))
                if (!detailFromMaster) {
                    refetchPromises.push(queryClient.refetchQueries({ queryKey: [detailEntity, id, 'detail'] }))
                }
                
                // Refetch additional queries specified in config
                if (action.refetchQueries && Array.isArray(action.refetchQueries)) {
                    action.refetchQueries.forEach(queryKey => {
                        refetchPromises.push(
                            queryClient.refetchQueries({ queryKey, exact: false })
                        )
                    })
                }
                
                // Execute all refetches
                await Promise.all(refetchPromises)
            }
            return result
        } catch (error) {
            console.error('Action failed:', error)
            return { error: error.message }
        }
    }
    
    // Loading states
    if (masterQuery.isLoading) {
        return <Loader />
    }
    
    if (masterQuery.isError) {
        return <div>Error loading {masterEntity}: {masterQuery.error?.message}</div>
    }
    
    const masterData = masterQuery.data?.data
    const detailData = detailFromMaster
        ? (masterData ? [masterData] : [])
        : (detailQuery.data?.data || [])
    
    // Update state with master and detail data
    const pageState = {
        ...state,
        master: masterData,
        detail: detailData
    }
    
    console.log('The View Config: ', viewConfig)

    return (
        <MasterDetailContainer width={viewConfig.width}>
            <MasterDetailTitle title={viewConfig.title} config={pageConfig} />
            <MasterDetailRow>
                <MasterView 
                    config={viewConfig.master} 
                    data={masterData}
                />
            </MasterDetailRow>
            <MasterDetailRow>
                <DetailView 
                    config={viewConfig.master?.detail} 
                    data={detailData}
                    isLoading={!detailFromMaster && detailQuery.isLoading}
                    onAction={handleAction}
                    pageState={pageState}
                    useStore={useStore}
                    masterData={masterData}
                />
            </MasterDetailRow>
        </MasterDetailContainer>
    )
}

export default MasterDetailView
